/*
 * File: components/admin/PayrollManagement.js
 * Mô tả: Component container gộp các chức năng "Duyệt Lương" và "Quản lý Lương T13".
 * Đã fix lỗi "subView is not defined" và loại bỏ menu ngang.
 */
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ApprovePayrollComponent from './ApprovePayroll';
import LuongT13ManagementComponent from './LuongT13Management';
import OtherDeductions from '../payroll/OtherDeductions';
import EmpSalaryBasicManagement from '../payroll/EmpSalaryBasicManagement';
import { apiAddOtherDeduction, apiGetOtherDeductions, apiDeleteOtherDeduction, apiGetAllEmployees } from '../../api';

export default function PayrollManagement() {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const activeTab = queryParams.get('tab') || 'approve';

    const [deductions, setDeductions] = useState([]);
    const [deductionLoading, setDeductionLoading] = useState(false);
    const [month, setMonth] = useState((new Date().getMonth() + 1).toString());
    const [year, setYear] = useState((new Date().getFullYear()).toString());

    // State for employeeId -> name mapping
    const [empMap, setEmpMap] = useState({});
    const [empMapLoading, setEmpMapLoading] = useState(false);

    // Fetch all employees once when deduction tab is opened
    useEffect(() => {
        if (activeTab === 'deduction') {
            setEmpMapLoading(true);
            apiGetAllEmployees().then(list => {
                const map = {};
                (list || []).forEach(e => { map[e.EMPID] = e.EMPNAM_VN; });
                setEmpMap(map);
            }).catch(() => setEmpMap({})).finally(() => setEmpMapLoading(false));
        }
    }, [activeTab]);

    const fetchDeductions = async () => {
        setDeductionLoading(true);
        try {
            // Chuẩn hóa tháng về dạng số nguyên không có số 0 đầu
            const normMonth = String(Number(month));
            const data = await apiGetOtherDeductions(normMonth, year);
            setDeductions(data);
        } catch {
            setDeductions([]);
        } finally {
            setDeductionLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'deduction') {
            fetchDeductions();
        }
    }, [activeTab, month, year]);

    const handleAddDeduction = async (form) => {
        try {
            // Chuẩn hóa tháng và năm khi gửi lên backend
            const normMonth = String(Number(form.month));
            const normYear = String(Number(form.year));
            await apiAddOtherDeduction({ ...form, month: normMonth, year: normYear });
            fetchDeductions();
            alert('Thêm khấu trừ thành công!');
        } catch {
            alert('Lỗi khi thêm khấu trừ!');
        }
    };

    const handleDeleteDeduction = async (id) => {
        if (!window.confirm('Xóa khoản trừ này?')) return;
        try {
            await apiDeleteOtherDeduction(id);
            fetchDeductions();
        } catch {
            alert('Lỗi khi xóa!');
        }
    };

    const formatCurrency = (amount) => (amount || 0).toLocaleString('vi-VN', { style: 'currency', currency: 'VND' });

    // State và logic lọc tìm kiếm khấu trừ
    const [deductionSearch, setDeductionSearch] = useState('');
    const filteredDeductions = deductionSearch
        ? deductions.filter(d => {
            const name = empMap[d.employeeId] || '';
            return (d.employeeId && d.employeeId.toUpperCase().includes(deductionSearch.toUpperCase())) ||
                (name && name.toLowerCase().includes(deductionSearch.toLowerCase())) ||
                (d.note && d.note.toLowerCase().includes(deductionSearch.toLowerCase()));
        })
        : deductions;

    return (
        <div className="animate-fade-in">
            {/* Hiển thị component dựa trên biến activeTab lấy từ URL Sidebar */}
            {activeTab === 'approve' && <ApprovePayrollComponent />}
            
            {activeTab === 'bonus' && <LuongT13ManagementComponent />}
            
            {activeTab === 'deduction' && (
                <div className="bg-white rounded-lg shadow p-6 max-w-3xl mx-auto">
                    <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
                        <div className="flex flex-col md:flex-row gap-2 md:items-center">
                            <label className="font-medium mr-2">Tháng</label>
                            <input
                                type="number"
                                min="1" max="12"
                                value={month}
                                onChange={e => setMonth(e.target.value)}
                                className="border p-2 rounded w-24 text-center outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="Tháng"
                            />
                        </div>
                        <div className="flex flex-col md:flex-row gap-2 md:items-center">
                            <label className="font-medium mr-2">Năm</label>
                            <input
                                type="number"
                                min="2000" max="2100"
                                value={year}
                                onChange={e => setYear(e.target.value)}
                                className="border p-2 rounded w-32 text-center outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="Năm"
                            />
                        </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200 shadow-sm">
                        <h3 className="font-semibold mb-3 text-lg text-indigo-700">Thêm khấu trừ lương khác</h3>
                        <OtherDeductions onSubmit={handleAddDeduction} month={month} year={year} />
                    </div>
                    
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
                        <h3 className="font-semibold text-lg text-indigo-700 mb-2 md:mb-0">Danh sách khấu trừ tháng {month}/{year}</h3>
                        <div className="flex gap-2 items-center">
                            <input
                                type="text"
                                placeholder="Tìm mã hoặc tên nhân viên..."
                                className="border border-gray-300 p-2 rounded-lg w-64 outline-none focus:ring-2 focus:ring-indigo-500"
                                value={deductionSearch}
                                onChange={e => setDeductionSearch(e.target.value)}
                            />
                            <button
                                className="bg-indigo-600 hover:bg-indigo-700 transition text-white px-4 py-2 rounded-lg shadow-sm"
                                onClick={() => setDeductionSearch(deductionSearch)}
                            >
                                Tìm kiếm
                            </button>
                            {deductionSearch && (
                                <button className="ml-2 text-gray-500 hover:text-gray-700 font-medium underline" onClick={() => setDeductionSearch('')}>Xóa lọc</button>
                            )}
                        </div>
                    </div>
                    
                    {deductionLoading ? (
                        <div className="py-6 flex justify-center">
                            <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-lg border border-gray-200">
                            <table className="min-w-full bg-white mb-0">
                                <thead className="bg-indigo-50 border-b border-indigo-100">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-indigo-800 uppercase tracking-wider">Mã NV</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-indigo-800 uppercase tracking-wider">Tên NV</th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-indigo-800 uppercase tracking-wider">Số tiền</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-indigo-800 uppercase tracking-wider">Ghi chú</th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-indigo-800 uppercase tracking-wider">Thao tác</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filteredDeductions.length > 0 ? filteredDeductions.map(d => (
                                        <tr key={d.id} className="hover:bg-indigo-50/50 transition">
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{d.employeeId}</td>
                                            <td className="px-4 py-3 text-sm text-gray-700">{empMap[(d.employeeId || '').toUpperCase().trim()] || <span className="text-gray-400 italic">Không rõ</span>}</td>
                                            <td className="px-4 py-3 text-sm font-semibold text-rose-600 text-right">-{formatCurrency(d.amount)}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{d.note}</td>
                                            <td className="px-4 py-3 text-sm text-center">
                                                <button onClick={() => handleDeleteDeduction(d.id)} className="text-rose-500 hover:text-rose-700 font-medium">Xóa</button>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="5" className="px-4 py-8 text-center text-gray-500 text-sm">Không có dữ liệu khấu trừ nào trong tháng này.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
            
            {activeTab === 'salary_basic' && <EmpSalaryBasicManagement />}
        </div>
    );
}